import { useCallback } from 'react'

import { useIs3dPrintModel, useRole } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useApiPut from 'Pods/Shared/Hooks/useApiPut'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'

import useUseCaseLogger from './useUseCaseLogger'
import { transformEmbeds } from '../Utils/embedTransformer'
import { parseValidationErrors } from '../Utils/errorMessages'
import { buildOptimisticEmbeds } from '../Utils/optimisticEmbeds'

/**
 * Custom hook for managing use case actions (create, update, delete, like/unlike, etc.).
 *
 * @param {Object} params - Hook parameters
 * @param {Object[]} params.useCases - Currently loaded use cases rendered in the sidebar.
 * @param {string} params.currentUsername - Username of the authenticated user.
 * @param {string} params.role - Current user role, affects notifications.
 * @param {number|string} params.productId - The product/item ID for API calls.
 * @param {Function} params.showLocalNotification - Callback to surface local (sidebar) notifications.
 * @param {Function} params.buildFormData - Factory that converts the pending media/options into FormData for POST/PUT.
 * @param {Function} params.clearPendingMedia - Resets local pending media state after successful submission.
 * @param {Function} params.getFormValues - Gets current form values for logging and submission.
 * @param {Function} params.resetForm - Clears the form fields after successful submission.
 * @param {Function} params.handleAdd - Insert/restore a use case in local state.
 * @param {Function} params.handleRemove - Remove a use case from local state.
 * @param {Function} params.handleUpdate - Update a use case in local state (likes, status, etc.).
 * @param {Function} [params.onCreateSuccess] - Optional callback invoked after the API confirms creation.
 */
export default function useUseCaseActions({
  useCases,
  currentUsername,
  productId,
  showLocalNotification,
  clearPendingMedia,
  buildFormData,
  getFormValues,
  resetForm,
  handleAdd,
  handleRemove,
  handleUpdate,
  onCreateSuccess,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const role = useRole()
  const { deleteData } = useApiDelete()
  const { getData } = useApiGet()
  const { postData } = useApiPost()
  const { putData, isLoading: isUpdating } = useApiPut()
  const { logError } = useUseCaseLogger(role, currentUsername)
  const { showSuccess, showError } = useNotifications()

  const handleMediaPost = async () => {
    const values = getFormValues()

    const formDataMetadata = {
      pendingMediaCount: values.pendingMedia?.length || 0,
      mediaSources: values.pendingMedia?.map(({ source }) => source).filter(Boolean) || [],
      hasSlicerFile: Boolean(values.slicerFile),
      technologyOption: values.technology?.value ?? values.technology ?? null,
      printerTypeId: values.printerType?.printerTypeId ?? values.printerType?.value ?? null,
      printerTypeName: values.printerType?.printerTypeName ?? values.printerType?.label ?? null,
      material: values.material?.value ?? values.material ?? null,
      printSuccess: values.printSuccess?.value ?? values.printSuccess ?? null,
      printTime: values.printTime?.value ?? values.printTime ?? null,
      usedSupport: values.usedSupport ?? false,
    }

    // Build FormData
    let formData
    try {
      formData = buildFormData()
    } catch (error) {
      logError('creation_build_form_data_failed', error, formDataMetadata)
      showLocalNotification('Failed to prepare form data. Please try again.', 'error')
      return
    }

    // Clear form immediately (sidebar closes after successful API response)
    clearPendingMedia()
    resetForm()

    // Show processing message (global notification that persists after sidebar closes)
    if (window.showNotification) {
      window.showNotification(
        is3dPrintModel ? 'Posting your 3D Make...' : 'Posting your use case...',
        {
          type: 'success',
          autohide: 5000,
        }
      )
    } else {
      showSuccess(is3dPrintModel ? 'Posting your 3D Make...' : 'Posting your use case...')
    }

    postData({
      path: `/api/internal/items/${productId}/item_use_cases/`,
      data: formData,
      onSuccess: () => {
        // Close sidebar after successful creation
        if (typeof onCreateSuccess === 'function') {
          onCreateSuccess()
        }

        // Show success notification after server confirms (global notification)
        if (window.showNotification) {
          if (role === 'seller') {
            window.showNotification(
              is3dPrintModel ? '3D Make posted successfully!' : 'Use case posted successfully!',
              {
                type: 'success',
                autohide: 5000,
              }
            )
          } else {
            window.showNotification(
              'You have successfully posted content on CGTrader. ' +
              'It will remain invisible to the public until we verify that everything is in order. ' +
              'After checking this 3D Make will appear publicly.',
              {
                type: 'success',
                autohide: 7000,
              }
            )
          }
        } else {
          if (role === 'seller') {
            showSuccess(is3dPrintModel ? '3D Make posted successfully!' : 'Use case posted successfully!')
          } else {
            showSuccess(
              'You have successfully posted content on CGTrader. ' +
              'It will remain invisible to the public until we verify that everything is in order. ' +
              'After checking this 3D Make will appear publicly.'
            )
          }
        }
      },
      onError: (error) => {
        const { status } = error.response || {}

        // Logging
        logError('creation_api_failed', error, {
          httpStatus: status,
          ...formDataMetadata,
        })

        // Show error notification after server responds (global notification)
        if (window.showNotification) {
          if (status === 422) {
            const errorMessage = parseValidationErrors(error, is3dPrintModel)
            window.showNotification(errorMessage, {
              type: 'error',
              autohide: 7000,
            })
          } else {
            window.showNotification(
              is3dPrintModel
                ? 'Failed to post 3D Make. Please try again.'
                : 'Failed to post use case. Please try again.',
              {
                type: 'error',
                autohide: 5000,
              }
            )
          }
        } else {
          if (status === 422) {
            const errorMessage = parseValidationErrors(error, is3dPrintModel)
            showError(errorMessage)
          } else {
            showError(
              is3dPrintModel
                ? 'Failed to post 3D Make. Please try again.'
                : 'Failed to post use case. Please try again.'
            )
          }
        }
      },
    })
  }

  const fetchPrinterTypes = useCallback(({ onSuccess, onError } = {}) => {
    getData({
      path: '/api/internal/printer_types',
      onSuccess,
      onError,
    })
  }, [getData])

  const handleLike = (productId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !productId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: true,
      likeCount: currentUseCase.likeCount + 1,
    })

    const likeEndpoint = `/api/internal/items/${productId}/item_use_cases/${useCaseId}/like`

    postData({
      path: likeEndpoint,
      data: {},
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('like_failed', error, {
          useCaseId,
          productId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: false,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }

  const handleUnlike = (productId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !productId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: false,
      likeCount: Math.max(0, currentUseCase.likeCount - 1),
    })

    const unlikeEndpoint = `/api/internal/items/${productId}/item_use_cases/${useCaseId}/like`

    deleteData({
      path: unlikeEndpoint,
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('unlike_failed', error, {
          useCaseId,
          productId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: true,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }

  const handleUseCaseApproval = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)

    handleUpdate(useCaseId, { status: 'accepted' })

    const formData = new FormData()
    formData.append('item_use_case[status]', 'accepted')

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: () => {
        showLocalNotification(
          is3dPrintModel ? '3D Make approved successfully' : 'Use case approved successfully',
          'success'
        )
      },
      onError: (error) => {
        logError('approval_failed', error, {
          useCaseId,
          previousStatus: originalUseCase?.status,
          httpStatus: error?.response?.status,
        })

        if (originalUseCase) {
          handleUpdate(useCaseId, { status: originalUseCase.status })
        }

        showLocalNotification(
          is3dPrintModel
            ? 'Failed to approve 3D Make. Please try again.'
            : 'Failed to approve Use Case. Please try again.',
          'error'
        )
      },
    })
  }

  const handleUseCaseDeletion = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)
    if (!originalUseCase) return

    handleRemove(useCaseId)

    deleteData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      onSuccess: () => {
        showLocalNotification(
          is3dPrintModel ? '3D Make deleted successfully' : 'Use case deleted successfully',
          'success'
        )
      },
      onError: (error) => {
        logError('deletion_failed', error, {
          useCaseId,
          httpStatus: error?.response?.status,
        })

        handleAdd(originalUseCase)
        showLocalNotification(
          is3dPrintModel
            ? 'Failed to delete 3D Make. Please try again.'
            : 'Failed to delete Use Case. Please try again.',
          'error'
        )
      },
    })
  }

  const handleUseCaseRejection = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)
    if (!originalUseCase) return

    handleRemove(useCaseId)

    const formData = new FormData()
    formData.append('item_use_case[status]', 'rejected')

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: () => {
        showLocalNotification(
          is3dPrintModel ? '3D Make rejected successfully' : 'Use case rejected successfully',
          'success'
        )
      },
      onError: (error) => {
        logError('rejection_failed', error, {
          useCaseId,
          previousStatus: originalUseCase.status,
          httpStatus: error?.response?.status,
        })

        handleAdd(originalUseCase)
        showLocalNotification(
          is3dPrintModel
            ? 'Failed to reject 3D Make. Please try again.'
            : 'Failed to reject Use Case. Please try again.',
          'error'
        )
      },
    })
  }

  const handleUseCaseUpdate = async (
    useCaseId,
    technologyValue,
    printerTypeId,
    printerTypeName,
    materialValue,
    printSuccessValue,
    descriptionValue,
    printTimeValue,
    usedSupportValue,
    onComplete,
    onError,
    mediaChanges = { added: [], removed: [] }
  ) => {
    const originalUseCase = useCases.find(({ id }) => id === useCaseId)
    if (!originalUseCase) return

    // Optimistic update
    const date = new Date().toISOString()

    const optimisticEmbeds = buildOptimisticEmbeds({
      originalEmbeds: originalUseCase.useCaseEmbeds,
      mediaChanges,
      date,
    })

    const optimisticUpdate = {
      technology: technologyValue,
      printerType: printerTypeId && printerTypeName ? {
        id: printerTypeId,
        name: printerTypeName,
        createdAt: originalUseCase.printerType?.createdAt || date,
        updatedAt: date,
        hidden: originalUseCase.printerType?.hidden || false,
        manufacturerId: originalUseCase.printerType?.manufacturerId || null,
      } : null,
      material: materialValue || null,
      printSuccess: printSuccessValue || null,
      description: descriptionValue || null,
      printTime: printTimeValue || null,
      usedSupport: usedSupportValue !== undefined ? usedSupportValue : false,
      useCaseEmbeds: optimisticEmbeds,
    }

    handleUpdate(useCaseId, optimisticUpdate)

    const formData = new FormData()
    if (technologyValue) {
      formData.append('item_use_case[technology]', technologyValue)
    }
    if (printerTypeId) {
      formData.append('item_use_case[printer_type_id]', printerTypeId)
    }
    if (materialValue) {
      formData.append('item_use_case[material]', materialValue)
    }
    if (printSuccessValue) {
      formData.append('item_use_case[print_success]', printSuccessValue)
    }
    if (descriptionValue?.trim()) {
      formData.append('item_use_case[description]', descriptionValue.trim())
    }
    if (printTimeValue) {
      formData.append('item_use_case[print_time]', printTimeValue)
    }
    if (usedSupportValue !== undefined && usedSupportValue !== null) {
      formData.append('item_use_case[used_support]', usedSupportValue.toString())
    }

    // Handle media changes (nested attributes for embeds)
    let embedAttributeIndex = 0

    // Add removed embeds (with _destroy: true)
    mediaChanges.removed.forEach((embedId) => {
      formData.append(`item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][id]`, embedId)
      formData.append(`item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][_destroy]`, 'true')
      embedAttributeIndex += 1
    })

    // Add new embeds
    mediaChanges.added.forEach((media) => {
      if (media.source === 'upload' && media.file) {
        // File upload
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][embed_url]`,
          media.file
        )
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][source]`,
          'upload'
        )
      } else if (media.externalId) {
        // External embed (YouTube, Vimeo, etc.)
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][external_id]`,
          media.externalId
        )
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][source]`,
          media.source
        )
      }
      formData.append(
        `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][position]`,
        embedAttributeIndex
      )
      embedAttributeIndex += 1
    })

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: (response) => {
        // Update with complete server response to reflect all changes immediately
        if (response?.data?.item_use_case) {
          const serverUseCase = response.data.item_use_case

          // Transform use_case_embeds from server (snake_case) to client format (camelCase)
          const mappedEmbeds = transformEmbeds(serverUseCase.use_case_embeds)

          // Map server response (snake_case) to client format (camelCase)
          handleUpdate(useCaseId, {
            technology: serverUseCase.technology,
            printerType: serverUseCase.printer_type ? {
              id: serverUseCase.printer_type.id,
              name: serverUseCase.printer_type.name,
              createdAt: serverUseCase.printer_type.created_at,
              updatedAt: serverUseCase.printer_type.updated_at,
              hidden: serverUseCase.printer_type.hidden,
              manufacturerId: serverUseCase.printer_type.manufacturer_id,
            } : null,
            material: serverUseCase.material,
            printSuccess: serverUseCase.print_success,
            description: serverUseCase.description,
            printTime: serverUseCase.print_time,
            usedSupport: serverUseCase.used_support,
            useCaseEmbeds: mappedEmbeds,
          })
        }
        showLocalNotification(
          is3dPrintModel ? '3D Make updated successfully' : 'Use case updated successfully',
          'success'
        )

        if (onComplete) onComplete()
      },
      onError: (error) => {
        logError('update_failed', error, {
          useCaseId,
          previousTechnology: originalUseCase.technology,
          previousPrinterType: originalUseCase.printerType?.id,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update
        handleUpdate(useCaseId, {
          technology: originalUseCase.technology,
          printerType: originalUseCase.printerType,
          material: originalUseCase.material,
          printSuccess: originalUseCase.printSuccess,
          description: originalUseCase.description,
          printTime: originalUseCase.printTime,
          usedSupport: originalUseCase.usedSupport,
          useCaseEmbeds: originalUseCase.useCaseEmbeds,
        })
        showLocalNotification(
          is3dPrintModel
            ? 'Failed to update 3D Make. Please try again.'
            : 'Failed to update Use Case. Please try again.',
          'error'
        )

        if (onError) onError()
      },
    })
  }

  return {
    handleLike,
    handleMediaPost,
    handleUnlike,
    handleUseCaseApproval,
    handleUseCaseDeletion,
    handleUseCaseRejection,
    handleUseCaseUpdate,
    fetchPrinterTypes,
    isUpdating,
  }
}
